import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

// IMS Logo URL
const IMS_LOGO_URL = "/IMSLOGO-2.png";
const HERO_IMAGE_URL = "https://static.prod-images.emergentagent.com/jobs/80293b25-1876-4660-bff0-f00ed258b393/images/69160fca834dbcf3fff2cc58b4ea214fef896fa0f82fa8c47ad7a0c6834264fe.png";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      // Redirect based on role
      if (result.user?.role === 'driver') {
        navigate('/driver');
      } else {
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-navy-950">
      {/* Left side - Hero Image (desktop) */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
      >
        <img 
          src={HERO_IMAGE_URL}
          alt="Fuel Distribution"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/80 via-navy-950/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16">
          <div className="flex items-center gap-4 mb-6">
            <img 
              src={IMS_LOGO_URL} 
              alt="IMS Logo" 
              className="h-14 w-auto"
            />
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-4">
            Tankerbase Next
          </h1>
          <p className="text-lg text-slate-200 max-w-md">
            Streamline your fuel distribution operations from order capture to delivery with our comprehensive platform.
          </p>
          <div className="mt-6 flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-slate-300 text-sm">Powered by Integer Micro Systems</span>
          </div>
        </div>
      </div>

      {/* Right side - Navy background with Login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Logo above modal */}
        <div className="mb-8 flex flex-col items-center">
          <img 
            src={IMS_LOGO_URL} 
            alt="IMS Logo" 
            className="h-16 w-auto mb-4"
          />
          <h1 className="text-2xl lg:hidden font-heading font-bold text-white">Tankerbase Next</h1>
        </div>
        
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading">Welcome back</CardTitle>
            <CardDescription>
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>Demo credentials:</p>
              <p className="font-mono text-xs mt-1">admin@fuelflow.com / admin123</p>
              <p className="font-mono text-xs mt-1">driver1@fuelflow.com / driver123</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
